"use client";
import { useId, useState } from "react";
import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { WohaRichText } from "../WohaRichText/WohaRichText";
import type { IAccordionListProps } from "./AccordionList.props";
export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );
  return (
    <div className="overflow-hidden rounded-xl border border-ash bg-white">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className="border-b border-ash last:border-b-0" key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="flex min-h-14 w-full cursor-pointer items-center justify-between gap-4 px-5 py-4 text-left text-base font-medium text-charcoal transition-colors hover:bg-paper-mist focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-electric-blue"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span>{item.title}</span>
                <Icon
                  className={cn("size-5 transition-transform duration-200", open && "rotate-45")}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-hidden={!open}
              aria-labelledby={triggerId}
              className={cn(
                "grid transition-[grid-template-rows] duration-300 ease-out",
                open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              )}
              id={panelId}
              inert={!open}
              role="region"
            >
              <div className="overflow-hidden">
                <WohaRichText
                  className="px-5 pb-5 text-base leading-7 text-steel"
                  content={item.content}
                />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
AccordionList.displayName = "Content::AccordionList";
